import { availabilityLine, storeLabels } from '../../lib/storeLinks';

/* ─── Data ─── */
export const POSE_CATEGORIES = [
  { name: 'Portrait', image: '/categories/portrait.webp', area: 'portrait' },
  { name: 'Fashion', image: '/categories/fashion.webp', area: 'fashion' },
  { name: 'Couple', image: '/categories/couple.webp', area: 'couple' },
  { name: 'Editorial', image: '/categories/editorial.webp', area: 'editorial' },
  { name: 'Maternity', image: '/categories/maternity.webp', area: 'maternity' },
  { name: 'Family', image: '/categories/family.webp', area: 'family' },
];

/* The workflow reads as one shoot, start to finish — not a feature list.
   Each chapter is identified by its `scene` (unique — the lists key on it,
   never on `phase`, since two chapters share the "During" phase). The
   fourth chapter is the photographer's hand-off (tracking the edit and
   sending it); the CLIENT-facing gallery experience — favourites,
   comments, downloads, the PIN, the demo — lives only in the Client
   galleries band below, so nothing is pitched twice. */
export const WORKFLOW_CHAPTERS = [
  {
    phase: 'Before',
    scene: 'The plan',
    kicker: 'Before the shoot',
    title: 'Plan it properly',
    body: 'Take the booking, define the session, and build the shot list straight from your saved poses.',
    details: [
      'Client, session type, and date on one booking',
      'Shot list built from your saved poses',
      'The whole job on one card — not scattered across notes apps',
    ],
    frame: 'Bookings',
    visual: 'bookings' as const,
    screenshot: '/screenshots/bookings.webp',
    alt: 'Muse bookings screen with session details, progress tracking, shot list, and client contacts',
  },
  {
    phase: 'During',
    scene: 'The shot list',
    kicker: 'On the day',
    title: 'Shoot with references up',
    body: 'Every must-have shot in one checklist, a saved reference pose linked to each frame — ticked off as you go.',
    details: [
      'Every must-have shot in one running checklist',
      'A saved reference pose linked to each frame',
      'Tick shots off with no signal — it syncs when you’re back online',
    ],
    frame: 'The shot list',
    visual: 'shotlist' as const,
    screenshot: '/screenshots/shotlist.webp',
    alt: 'Muse shot list screen with reference poses linked to each frame and live progress',
  },
  {
    phase: 'During',
    scene: 'The library',
    kicker: 'On the day',
    title: 'A barn with no signal',
    body: 'Mark a collection for offline and it opens instantly with no signal at all — a barn wedding changes nothing.',
    details: [
      'Save for Offline keeps a collection on your phone',
      'Opens with zero signal — airplane mode included',
      'No spinner, no “no connection” — it’s already there',
    ],
    frame: 'Saved collections',
    visual: 'collect' as const,
    screenshot: '/screenshots/collect.webp',
    alt: 'Muse collections screen showing saved pose references',
  },
  {
    phase: 'After',
    scene: 'The delivery',
    kicker: 'After the shoot',
    title: 'Hand it over properly',
    body: 'Track the edit from culling to delivered on the same booking, then send the gallery the moment it’s ready.',
    details: [
      'Culling, editing, delivered — tracked on the booking',
      'The gallery is built from the booking it belongs to',
      'Send the link the moment the edit is done',
    ],
    frame: 'The hand-off',
    visual: 'gallery' as const,
    screenshot: null,
    alt: 'A Muse client gallery with a cover photograph and title',
  },
];

/* Getting-started — a film-leader countdown, not icon cards. Each
   step's dial arc fills a third further (01 → ⅓, 02 → ⅔, 03 → full):
   three steps and the dial is complete. The dial numeral is the only
   index; the kicker reads as a timeline (Tonight / This week / Shoot
   day). Every ledger line restates a claim already made elsewhere on
   the page — nothing invented. */
export const HOW_IT_WORKS = [
  {
    step: '01',
    sweep: 1 / 3,
    kicker: 'Tonight',
    title: 'Get the app',
    description: `${availabilityLine()} Sign in with email, Google, or Apple and you’re in.`,
    ledger: storeLabels().note,
  },
  {
    step: '02',
    sweep: 2 / 3,
    kicker: 'This week',
    title: 'Build your collections',
    description:
      'Browse what other photographers are posting and save the poses you’d actually use, sorted however makes sense to you.',
    ledger: 'Synced to your account · Save for Offline for the day',
  },
  {
    step: '03',
    sweep: 1,
    kicker: 'Shoot day',
    title: 'Take it on a shoot',
    description:
      'Pull your collections up on set, post your own work afterwards, and throw a shot into the current challenge if you’re feeling competitive.',
    ledger: 'Zero signal on set changes nothing',
  },
];

export interface FAQEntry {
  question: string;
  answer: string;
  guideAnchor?: string;
}

export const FAQ_ITEMS: FAQEntry[] = [
  {
    question: 'Is Muse free to use?',
    answer:
      'Yes. The discovery feed, saving poses, collections, challenges, and stories are free, and the free tier never expires. Every new account also starts with 30 days of Pro+ on us — no card, nothing auto-charges — and when it ends you simply drop back to Free. Pro raises the limits — more pose views, more uploads, bigger collections. Pro+ adds unlimited everything plus the professional tools: booking management, client galleries, and pricing templates — worth it once Muse is part of how you actually work, not before.',
    guideAnchor: 'plans',
  },
  {
    question: 'What types of photography does Muse support?',
    answer:
      'Anything with people in it, mostly: weddings, couples, engagement, maternity, family, fashion, editorial, headshots. The library grows with whatever the community uploads, and everything is tagged, so you can search for exactly the session you have booked.',
    guideAnchor: 'discover',
  },
  {
    question: 'Can I use Muse offline during shoots?',
    answer:
      'Yes — this was the original reason we built it. Mark a pose or collection as Save for Offline and it’s stored on your phone, so it opens with no signal at all. Booking edits and shot-list ticks you make on location sync the next time you’re online.',
    guideAnchor: 'offline',
  },
  {
    question: 'How does the booking system work?',
    answer:
      'You create a booking with the client’s details and session type, then attach a shot list built from your saved poses. After the shoot, the same booking tracks where you’re at — culling, editing, delivered — so nothing sits forgotten on a memory card.',
    guideAnchor: 'bookings',
  },
];
